import { useCallback } from "react";

import { useSearchParams } from "react-router-dom";

import { FilterMode } from "src/cases/caseView/blocks/RelatedEventsFilterDropdown";

type UseFilterModeUrlOptions = {
  blockKey: string;
  eventType: string;
};

type UseFilterModeUrlReturn = {
  getFilterMode: (hasFilterCondition: boolean) => FilterMode;
  setFilterMode: (mode: FilterMode) => void;
  urlParamKey: string;
};

export const getFilterModeUrlParamKey = (blockKey: string, eventType: string) =>
  `mode-${blockKey}-${eventType}`;

export const useFilterModeUrl = ({
  blockKey,
  eventType,
}: UseFilterModeUrlOptions): UseFilterModeUrlReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlParamKey = getFilterModeUrlParamKey(blockKey, eventType);
  const isPinned = searchParams.get(urlParamKey) === "pinned";

  const getFilterMode = useCallback(
    (hasFilterCondition: boolean): FilterMode => {
      if (isPinned) {
        return "pinned";
      }
      if (hasFilterCondition) {
        return "advanced_filters";
      }
      return "all";
    },
    [isPinned],
  );

  // Update URL with new filter mode
  const setFilterMode = useCallback(
    (mode: FilterMode) => {
      setSearchParams((params) => {
        if (mode === "pinned") {
          // Only persist "pinned" mode in URL
          params.set(urlParamKey, "pinned");
        } else {
          // Remove param for "all" and "advanced_filters" (advanced_filters is inferred from hasFilterCondition)
          params.delete(urlParamKey);
        }
        return params;
      });
    },
    [setSearchParams, urlParamKey],
  );

  return {
    getFilterMode,
    setFilterMode,
    urlParamKey,
  };
};
